import React, { useEffect, useState } from 'react';
import { Modal } from '@storaensods/se-design-system';
import icons from './../../../icons.svg';

import { Button } from '../../common/utils';
import CalibrationProductDrawer from '../../calibration/segments/calibrationProductDrawer';
import { isScaleShelfType } from '../../../utils/shelf-config';

const MultipleProdutsSlots = ({
  isOpen,
  close,
  cabinetActions,
  cabinetSlotData,
  shelfNumber,
  allProducts,
  t,
  cabinet,
  scaleShelves = [],
}) => {
  const [selectedItemCount, setSelectedItemCount] = useState(0);
  // scale2: the refiller may change the product of a lane while confirming; null = keep the slot's product
  const [replacementProduct, setReplacementProduct] = useState(null);
  const [showProductDrawer, setShowProductDrawer] = useState(false);

  const isScaleCabinet =
    cabinet.deviceSubTypeName === 'scale' ||
    cabinet.deviceSubTypeName === 'hybrid';
  const isScale2 = cabinet.scaleGeneration === 'scale2';

  const handleProductCountChange = type => {
    if (type === 'plus') {
      setSelectedItemCount(selectedItemCount + 1);
    } else {
      // a slot count cannot go below zero (a negative quantity would otherwise be sent to the cabinet)
      setSelectedItemCount(Math.max(0, selectedItemCount - 1));
    }
  };

  const scaleItems = Array.isArray(cabinet.currentItems)
    ? cabinet.currentItems.filter(i => i.shelf && i.slot && i.barcode)
    : [];

  let mergedScaleItem = scaleItems.reduce((acc, item) => {
    const existing = acc.find(
      x => x.shelf === item.shelf && x.slot === item.slot
    );

    if (existing) {
      existing.count++;
    } else {
      acc.push({ shelf: item.shelf, slot: item.slot, count: 1 });
    }

    return acc;
  }, []);

  let scaleItem = mergedScaleItem.find(
    i =>
      i.shelf === Number(shelfNumber) && i.slot === Number(cabinetSlotData.slot)
  );
  // scale2 slots carry the device's trusted count in the planogram (re-uploaded per door close); the
  // inventory snapshot is the fallback, and the only source for scale1 as before
  let itemCount = Number.isInteger(cabinetSlotData.currentCount)
    ? cabinetSlotData.currentCount
    : (scaleItem && scaleItem.count) || 0;

  useEffect(() => {
    setSelectedItemCount(itemCount);
  }, [itemCount]);

  useEffect(() => {
    // a newly opened slot starts from its own product
    setReplacementProduct(null);
    setShowProductDrawer(false);
  }, [shelfNumber, cabinetSlotData.slot, isOpen]);

  let products =
    Array.isArray(cabinetSlotData.products) && cabinetSlotData.products.length
      ? cabinetSlotData.products
      : cabinetSlotData.productAssigned
      ? [cabinetSlotData.productAssigned]
      : [];

  products = products.reduce((accumulator, current) => {
    if (Object.keys(accumulator).includes(current)) {
      accumulator[current] = accumulator[current] + 1;
    } else {
      accumulator[current] = 1;
    }

    return accumulator;
  }, {});

  const getScaleCabinetNote = () => {
    if (cabinet.deviceSubTypeName !== 'scale') return;
    if (!scaleShelves.find(shelf => shelf.shelf === shelfNumber)) return;

    const { products, productAssigned } = cabinetSlotData;

    //if a product has been added in the planogram, but not yet calibrated to the slot show the attention message
    if (products && products[0] && !productAssigned) {
      return (
        <div className="planogram__modal-info">
          <svg className="planogram__slots-item-cube">
            <use xlinkHref={`${icons}#icon-balance-scale`}></use>
          </svg>
          <p>{t('common:slotNotAssigned')}</p>
        </div>
      );
    }
    //if a product in plangogram is different than the claibrated product show warning.
    if (
      products &&
      products[0] &&
      productAssigned &&
      products[0] !== productAssigned
    ) {
      return (
        <div className="planogram__modal-info planogram__modal-info--warn">
          <svg className="planogram__slots-item-cube">
            <use xlinkHref={`${icons}#icon-warning`}></use>
          </svg>
          <p>{t('common:productMismatched')}</p>
        </div>
      );
    }

    //if a product is assinged but not yet in the planogram, show warning too.
    if (productAssigned && products && !products[0]) {
      return (
        <div className="planogram__modal-info">
          <svg className="planogram__slots-item-cube">
            <use xlinkHref={`${icons}#icon-warning`}></use>
          </svg>
          <p>{t('common:productMissingInPlanogram')}</p>
        </div>
      );
    }

    // if product is not defined in planogram nor assigned in the  cabinet slot
    if (!products[0] && !productAssigned) {
      return (
        <div className="planogram__modal-info planogram__modal-info--empty">
          <svg className="planogram__slots-item-cube">
            <use xlinkHref={`${icons}#icon-balance-scale`}></use>
          </svg>
          <p>{t('common:productMissing')}</p>
        </div>
      );
    }
  };

  const isShelf = scaleShelves.some(
    p => p.shelf === Number(shelfNumber) && isScaleShelfType(p.type)
  );

  // scale1: corrections only outside a replenishment (the reader is trained separately).
  // scale2: the product and count are confirmed during the refill itself, so also while replenishment is on.
  const canCorrect =
    isScaleCabinet &&
    isShelf &&
    (cabinet.replenishment === 'off' ||
      (isScale2 && cabinet.replenishment === 'on'));

  const sendCorrection = productId =>
    cabinetActions.sendInventoryCorrection(
      cabinet.deviceCode,
      cabinet.group,
      shelfNumber,
      cabinetSlotData.slot,
      productId,
      selectedItemCount
    );

  const renderCountControls = productId => (
    <>
      <div className="calibration__product-input">
        <button onClick={() => handleProductCountChange('minus')}>-</button>
        <input
          type={isScale2 ? 'number' : 'numnber'}
          min={0}
          max={isScale2 ? undefined : 2}
          value={selectedItemCount}
          disabled={!isScale2}
          onChange={e => {
            const value = parseInt(e.target.value, 10);
            setSelectedItemCount(Number.isNaN(value) || value < 0 ? 0 : value);
          }}
        />
        <button onClick={() => handleProductCountChange('plus')}>+</button>
      </div>
      <button
        className="calibration__selected"
        onClick={() => sendCorrection(productId)}
      >
        <span className="calibration__selected-shelf">
          {cabinet.inventoryCorrectionLoading === true
            ? t('common:updating')
            : t('common:updateSlotInventory')}
        </span>
      </button>
    </>
  );

  // scale2: the lane's product row, with the device facts and the option to change the product
  // scale2: the product currently on the lane, or the replacement the refiller picked
  const slotProductId =
    cabinetSlotData.productAssigned ||
    (Array.isArray(cabinetSlotData.products) && cabinetSlotData.products[0]) ||
    null;
  const scale2Product =
    replacementProduct ||
    (allProducts || []).find(p => p.id === slotProductId) ||
    null;

  const renderScale2Lane = () => {
    const product = scale2Product;

    return (
      <li key={`lane-${cabinetSlotData.laneId || cabinetSlotData.slot}`}>
        {product ? (
          <>
            <div className="planogram__modal-product">
              <img src={product.imageUrl} alt="product" />
            </div>
            <span>
              <strong>{product.name}</strong>
            </span>
          </>
        ) : (
          <span>
            <strong>{t('common:productMissing')}</strong>
          </span>
        )}
        {canCorrect && (
          <button
            className="calibration__selected"
            onClick={() => setShowProductDrawer(true)}
          >
            <span className="calibration__selected-shelf">
              {t('common:changeProduct')}
            </span>
          </button>
        )}
        {Number.isInteger(cabinetSlotData.stockedCount) && (
          <span>
            {t('common:stockedCount')}: {cabinetSlotData.stockedCount}
          </span>
        )}
        {typeof cabinetSlotData.confidence === 'number' && (
          <span>
            {t('common:deviceConfidence')}:{' '}
            {Math.round(cabinetSlotData.confidence * 100)}%
          </span>
        )}
        {canCorrect && product && renderCountControls(product.id)}
      </li>
    );
  };

  return (
    <Modal
      active={isOpen}
      centered={true}
      size="md"
      className="planogram__modal"
      onClose={() => close()}
    >
      <div className="calibration__selected">
        <span className="calibration__selected-shelf">
          {t('common:shelf')}: {shelfNumber}
        </span>
        <span className="calibration__selected-slot">
          {t('common:slot')}: {cabinetSlotData.slot}
        </span>
      </div>
      <div className="planogram__modal-body">
        {isScale2 && isShelf && canCorrect && (
          <p className="planogram__modal-info">
            {t('common:placeItemsThenConfirm')}
          </p>
        )}
        <ul className="planogram__modal-names">
          {isScale2 && isShelf
            ? renderScale2Lane()
            : Object.keys(products)
                .filter(productId => allProducts.find(p => p.id === productId))
                .map(id => {
                  const selectedProduct = allProducts.find(
                    product => product.id === id
                  );
                  return (
                    <li key={selectedProduct.id}>
                      <div className="planogram__modal-product">
                        <img src={selectedProduct.imageUrl} alt="product" />
                        {products[id] && products[id] > 1 && (
                          <span>{products[id]}</span>
                        )}
                      </div>
                      <span>
                        <strong>{selectedProduct.name}</strong>
                      </span>
                      {canCorrect && renderCountControls(selectedProduct.id)}
                    </li>
                  );
                })}
        </ul>
      </div>
      {isScaleCabinet && !isScale2 && getScaleCabinetNote()}
      <div className="planogram__modal-footer">
        <Button type="close" onClick={() => close()} />
      </div>
      {isScale2 && isShelf && (
        <CalibrationProductDrawer
          products={allProducts || []}
          t={t}
          shelf={shelfNumber}
          slot={cabinetSlotData.slot}
          showProductDrawer={showProductDrawer}
          closeProductDrawer={state => setShowProductDrawer(state)}
          productSelection={product => {
            setReplacementProduct({
              id: product.id,
              barcode: product.barcode,
              name: product.name,
              imageUrl: product.imageUrl,
            });
            setShowProductDrawer(false);
          }}
          selectedProduct={scale2Product || {}}
        />
      )}
    </Modal>
  );
};

export default MultipleProdutsSlots;
